/**
 * Login Screen — programmatic DOM, dark theme matching existing UI.
 * Shown when user is not authenticated.
 * Supports email/password + Google OAuth.
 */

import { authManager } from './auth-manager';

let loginContainer: HTMLElement | null = null;

export function showLoginScreen(): void {
  if (loginContainer) return;

  loginContainer = document.createElement('div');
  loginContainer.id = 'login-screen';
  loginContainer.innerHTML = `
    <div class="login-card">
      <h1 class="login-logo">Hatchlings</h1>
      <p class="login-subtitle">Your AI team, always on.</p>

      <form class="login-form" id="login-form">
        <input
          class="login-input"
          type="email"
          id="login-email"
          placeholder="Email address"
          autocomplete="email"
          required
        />
        <input
          class="login-input"
          type="password"
          id="login-password"
          placeholder="Password"
          autocomplete="current-password"
          required
        />
        <button class="login-btn login-btn-primary" type="submit" id="login-submit">
          Sign In
        </button>
        <div class="login-error" id="login-error"></div>
      </form>

      <div class="login-divider">
        <span>or</span>
      </div>

      <button class="login-btn login-btn-google" id="login-google">
        Continue with Google
      </button>

      <p class="login-toggle" id="login-toggle">
        Don't have an account? <a href="#">Sign up</a>
      </p>
    </div>
  `;

  document.body.appendChild(loginContainer);

  // Wire up events
  const form = loginContainer.querySelector('#login-form') as HTMLFormElement;
  const emailInput = loginContainer.querySelector('#login-email') as HTMLInputElement;
  const passwordInput = loginContainer.querySelector('#login-password') as HTMLInputElement;
  const submitBtn = loginContainer.querySelector('#login-submit') as HTMLButtonElement;
  const errorEl = loginContainer.querySelector('#login-error') as HTMLElement;
  const googleBtn = loginContainer.querySelector('#login-google') as HTMLButtonElement;
  const toggleEl = loginContainer.querySelector('#login-toggle') as HTMLElement;

  let isSignUp = false;

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    errorEl.textContent = '';
    submitBtn.disabled = true;
    submitBtn.textContent = isSignUp ? 'Creating account...' : 'Signing in...';

    const email = emailInput.value.trim();
    const password = passwordInput.value;

    const result = isSignUp
      ? await authManager.signUp(email, password)
      : await authManager.signIn(email, password);

    if (result.error) {
      errorEl.textContent = result.error;
      submitBtn.disabled = false;
      submitBtn.textContent = isSignUp ? 'Sign Up' : 'Sign In';
    }
    // On success, authManager fires onAuthChange → main.ts handles the rest
  });

  googleBtn.addEventListener('click', async () => {
    const result = await authManager.signInWithGoogle();
    if (result.error) {
      errorEl.textContent = result.error;
    }
  });

  toggleEl.addEventListener('click', (e) => {
    e.preventDefault();
    isSignUp = !isSignUp;
    submitBtn.textContent = isSignUp ? 'Sign Up' : 'Sign In';
    toggleEl.innerHTML = isSignUp
      ? 'Already have an account? <a href="#">Sign in</a>'
      : 'Don\'t have an account? <a href="#">Sign up</a>';
    errorEl.textContent = '';
  });
}

export function hideLoginScreen(): void {
  if (loginContainer) {
    loginContainer.remove();
    loginContainer = null;
  }
}

// Inject login screen styles
const loginStyles = document.createElement('style');
loginStyles.textContent = `
  #login-screen {
    position: fixed; inset: 0; z-index: 200;
    display: flex; align-items: center; justify-content: center;
    background: #1a1a2e;
    font-family: system-ui, -apple-system, sans-serif;
  }
  .login-card {
    width: 100%; max-width: 380px; padding: 40px 32px;
    background: rgba(20, 20, 40, 0.95);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 16px;
    backdrop-filter: blur(16px);
    text-align: center;
  }
  .login-logo {
    font-size: 2rem; font-weight: 800; color: #fff;
    margin-bottom: 4px; letter-spacing: -0.02em;
  }
  .login-subtitle {
    color: rgba(255,255,255,0.4); font-size: 0.9rem;
    margin-bottom: 32px;
  }
  .login-form { display: flex; flex-direction: column; gap: 12px; }
  .login-input {
    width: 100%; padding: 12px 16px; border-radius: 8px;
    background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12);
    color: #fff; font-size: 0.9rem; outline: none; font-family: inherit;
    box-sizing: border-box;
  }
  .login-input::placeholder { color: rgba(255,255,255,0.25); }
  .login-input:focus { border-color: rgba(99, 102, 241, 0.5); }
  .login-btn {
    width: 100%; padding: 12px; border-radius: 8px;
    font-size: 0.9rem; font-weight: 600; cursor: pointer;
    border: none; transition: transform 0.15s, background 0.2s;
    font-family: inherit; box-sizing: border-box;
  }
  .login-btn:hover { transform: translateY(-1px); }
  .login-btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
  .login-btn-primary {
    background: #6366f1; color: #fff;
    box-shadow: 0 2px 12px rgba(99, 102, 241, 0.3);
  }
  .login-btn-primary:hover { background: #5558e8; }
  .login-btn-google {
    background: rgba(255,255,255,0.08); color: #fff;
    border: 1px solid rgba(255,255,255,0.15);
  }
  .login-btn-google:hover { background: rgba(255,255,255,0.12); }
  .login-error {
    color: #f87171; font-size: 0.8rem; min-height: 1.2em;
    text-align: left;
  }
  .login-divider {
    display: flex; align-items: center; gap: 12px;
    margin: 20px 0; color: rgba(255,255,255,0.25); font-size: 0.8rem;
  }
  .login-divider::before, .login-divider::after {
    content: ''; flex: 1; height: 1px;
    background: rgba(255,255,255,0.1);
  }
  .login-toggle {
    margin-top: 20px; font-size: 0.8rem; color: rgba(255,255,255,0.4);
  }
  .login-toggle a {
    color: #818cf8; text-decoration: none;
  }
  .login-toggle a:hover { text-decoration: underline; }
`;
document.head.appendChild(loginStyles);
